import { useState } from 'react';
import { useEffect } from 'react';
import MTSControls from './mtsControls';
import ECDSAcuityControls from './ecdsAcuityControls';
import { isMTSVersion } from './acuityScoreUtils';

/**
 * AcuityScoreControls component that conditionally renders either MTS or ECDS acuity controls
 * based on configuration settings and the form's start date.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} [props.value] - The current form value containing acuity score data
 * @param {Object} [props.value.firstDiscriminator] - The first discriminator object used to determine acuity version
 * @param {boolean} [props.value.firstDiscriminator.ecdsVersion] - Flag indicating if ECDS version is used
 * @param {Function} props.onChange - Callback function triggered when form value changes
 * @param {boolean} props.canEdit - Whether the form fields can be edited
 * @param {boolean} props.isError - Whether the form has validation errors
 * @param {boolean} props.isSmall - Whether to render in compact/small mode
 * @param {string} props.startDate - The start/admit date used to determine which acuity version to display
 * @returns {JSX.Element} Either MTSControls or ECDSAcuityControls based on configuration and dates
 *
 * @description
 * This component uses configuration settings (modules.acuity.mtsEnabled, modules.acuity.useECDSAcuitySince,
 * modules.acuity.useMTSSince) to determine whether to show MTS or ECDS acuity controls.
 * For existing forms, it checks for the presence of 'ecdsVersion' property.
 * For new forms, it compares the startDate against the configured threshold dates.
 */
const AcuityScoreControls = ({ value, onChange, canEdit, isError, isSmall, startDate, noMargin, isTriage = true }) => {
    const [showMTS, setShowMTS] = useState(false);

    useEffect(() => {
        setShowMTS(isMTSVersion(value, startDate));
    }, [value?.firstDiscriminator]);

    return (
        <>
            {showMTS ? (
                <MTSControls
                    value={value}
                    onChange={onChange}
                    canEdit={canEdit}
                    isError={isError}
                    isSmall={isSmall}
                    noMargin={noMargin}
                    isTriage={isTriage}
                />
            ) : (
                <ECDSAcuityControls
                    value={value}
                    onChange={onChange}
                    canEdit={canEdit}
                    isError={isError}
                    startDate={startDate}
                    noMargin={noMargin}
                />
            )}
        </>
    );
};

export default AcuityScoreControls;
